import React, { useCallback, useMemo } from 'react';
import eventBus from '@sd-utility/eventbus';
import { ProductCard as FuseProductCard } from '@tvg/fuse/product-card';
import { PLP_UPDATE_EVENT } from 'client/config';
import { addRoundel, formatReviewCount, makeSimple } from 'client/utils/utils';
import { addProductToSaveForLater, removeProductFromSaveForLater } from 'common/save-for-later-service';
import { Product } from 'common/types/search-response';
import { useQueryClient } from '@tanstack/react-query';

function ProductCardComponent({
  product,
  productIndex,
  isSaved,
  globalBrand,
  isSkeleton,
}: {
  product: Product;
  productIndex: number;
  isSaved: boolean;
  globalBrand?: string;
  isSkeleton: boolean;
}) {
  const {
    product_id,
    catalogueNumber,
    variation_id,
    url,
    brand,
    title,
    image,
    price,
    productReviews,
    swatchSources,
    roundelId,
    promotionIdentifier,
  } = product;

  const queryClient = useQueryClient();

  const swatches = swatchSources?.length > 1 ? swatchSources : [];

  const reviewsCount = productReviews?.reviewsCount;
  const formattedReviewCount = formatReviewCount(reviewsCount);
  const reviewText =
    formattedReviewCount && reviewsCount ? `${formattedReviewCount} ${reviewsCount > 1 ? 'reviews' : 'review'}` : '';

  const displaySavePriceInd = price?.displaySavePriceInd;
  const priceShowSave = typeof displaySavePriceInd === 'string' ? displaySavePriceInd === 'true' : displaySavePriceInd;

  const isTopPriorityProduct = productIndex < 3;

  const titleMemo = useMemo(() => makeSimple(title), [title]);

  const imageMemo = useMemo(
    () =>
      ({
        url: addRoundel(image?.url, roundelId, globalBrand),
        altText: makeSimple(image?.alt_text),
        disabledLazy: isTopPriorityProduct,
        fetchPriority: isTopPriorityProduct ? 'high' : 'auto',
      } as const),
    [image?.url, roundelId, globalBrand, image?.alt_text, isTopPriorityProduct],
  );

  const priceMemo = useMemo(
    () => ({
      current: price?.current,
      previous: price?.previous,
      saving: price?.saving,
      showCurrentPriceFrom: price?.currentFrom,
      showPreviousPriceFrom: price?.previousFrom,
      showSave: priceShowSave,
      isEuro: price?.currencyCode === 'EUR',
    }),
    [price, priceShowSave],
  );

  const starsMemo = useMemo(
    () => ({
      rating: productReviews?.averageStars,
      reviewText,
    }),
    [productReviews?.averageStars, reviewText],
  );

  const handleSaveClick = useCallback(
    async (newSaved: boolean) => {
      eventBus.publish(PLP_UPDATE_EVENT);
      const skuIDString = variation_id.toString();

      try {
        if (newSaved) {
          await addProductToSaveForLater(skuIDString);
        } else {
          await removeProductFromSaveForLater(skuIDString);
        }

        // Invalidate SFL query so it refetches automatically
        queryClient.invalidateQueries({ queryKey: ['sfl'] });
      } catch (error) {
        console.error('Error updating save for later:', error);
      }
    },
    [variation_id, queryClient],
  );

  const sflMemo = useMemo(
    () => ({
      isSaved,
      saveCallback: handleSaveClick,
    }),
    [isSaved, handleSaveClick],
  );

  const promotionIdentifierMemo = useMemo(
    () => ({
      colour: promotionIdentifier?.colour,
      displayName: promotionIdentifier?.displayName || '',
    }),
    [promotionIdentifier?.colour, promotionIdentifier?.displayName],
  );

  return (
    <FuseProductCard
      catalogueId={catalogueNumber}
      productUrl={url}
      isSkeleton={isSkeleton}
      brand={brand}
      title={titleMemo}
      image={imageMemo}
      swatches={swatches}
      price={priceMemo}
      stars={starsMemo}
      sfl={sflMemo}
      promotionIdentifier={promotionIdentifierMemo}
      dataTestId="gallery-product-card"
      data-cnstrc-item-id={product_id}
      data-cnstrc-item-name={titleMemo}
      data-cnstrc-item-variation-id={variation_id}
      data-cnstrc-item-price={price?.current}
      slotProps={{ saveForLater: { 'data-cnstrc-btn': 'add_to_wishlist' } }}
    />
  );
}

export const ProductCard = React.memo(ProductCardComponent);
